// Standard library imports
import React, { useEffect, useState, useRef } from 'react';

// External library imports
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { get as lodashGet, set as lodashSet, cloneDeep } from 'lodash';
import moment from 'moment';

// Internal module imports
import { useLoader, useUserRole } from '../../../../../hooks';
import Dropdown from '../../../../../components/Inputs/Dropdown';
import DatePicker from '../../../../../components/Inputs/DatePicker/DatePicker';
import Typography from '../../../../../components/Typography/Typography';
import { Input, TextArea, ToggleButton } from '../../../../../components/Inputs/Input';
import { momentTimeFormater } from '../../../../../utils/timeHelper';
import bidClasses from '../../Bidding.module.css';
import allDeviceClasses from '../../../../../styles/AllDevices.module.css';
import { getTotalHoursForDay } from '../../../../../utils/dateHelper';
import { CreateBundleBidValidator } from '../../../../../validations/BiddingService/Bidding';
import { RootBundleBiddingService } from '../../../../../services/BiddingService/Root/BundleBiddingService';
import { AdminBundleBiddingService } from '../../../../../services/BiddingService/Admin/BundleBiddingService';

const BID_TYPES = [
    {
        label: 'Primary',
        value: 'primary',
    },
    {
        label: 'Secondary',
        value: 'secondary',
    },
];

const getInitialValues = (date, market, instances = [], capacity = {}, price = [], regulationPrice = [], hidePrice = false) => {
    const totalHours = getTotalHoursForDay(momentTimeFormater(date).format('YYYY-MM-DD'));
    let temp = {
        capacity: {},
        price: [],
        ...(market.includes('aFRR') || market.includes('mFRR') ? { regulationPrice: [] } : {}),
    };
    if (price.length === totalHours) {
        temp = {
            capacity: capacity,
            price: price,
            ...(market.includes('aFRR') || market.includes('mFRR') ? { regulationPrice: regulationPrice } : {}),
        };
    } else {
        Array.from({ length: totalHours }, (__, idx) => {
            instances.map((ins) => {
                lodashSet(temp, ['capacity', ins.instanceId, idx], lodashGet(capacity, [ins.instanceId, idx]));
            });
            temp.price.push(lodashGet(price, [idx]));
            if (market.includes('aFRR') || market.includes('mFRR')) temp.regulationPrice.push(lodashGet(regulationPrice, [idx]));
        });
    }

    if (hidePrice) temp.price = [];

    return temp;
};

export const CreateBundleBid = ({ data, closeModal, getBids, editBid = false, existingBid = null, editFunc = null }) => {
    const formikRef = useRef();
    const { isRootUser } = useUserRole();
    const service = isRootUser ? RootBundleBiddingService : AdminBundleBiddingService;
    const { market, instanceBundle, bidDate, cost = 0 } = data;
    const [pairs, setPairs] = useState(instanceBundle.pairs || []);
    const isFRR = market.includes('aFRR') || market.includes('mFRR');
    const hidePrice = isFRR && pairs.some((p) => !!p.energyMarketOnly);
    const [startLoader, stopLoader] = useLoader();
    const defaultDate = bidDate ? moment(bidDate).toDate() : moment().toDate();
    const [date, setDate] = useState(defaultDate);
    const [selectedBidType, setSelectedBidType] = useState(BID_TYPES[0]);
    const [alreadyBidCreatedBids, setAlreadyBidCreatedBids] = useState([]);
    const [bidRequest, setBidRequest] = useState({ note: null });

    const [approvedCapacity, setApprovedCapacity] = useState({});

    const [lastBidData, setLastBidData] = useState({
        capacity: {},
        price: [],
        regulationPrice: [],
    });

    const [initialValues, setInitialValues] = useState({
        capacity: {},
        price: [],
        ...(!isFRR ? { reserveBidIdentification: '' } : { regulationPrice: [] }),
        bidType: null,
        isExternal: false,
    });

    const [datahubCostEnergy, setDatahubCostEnergy] = useState([]);

    const [alreadyBidCreatedHours, setAlreadyBidCreatedBidsHours] = useState([]);

    useEffect(() => {
        setInitialValues(() => ({
            ...getInitialValues(
                date,
                market,
                pairs,
                editBid ? lodashGet(existingBid, ['capacity'], {}) : [],
                editBid ? lodashGet(existingBid, ['price'], []) : [],
                editBid ? lodashGet(existingBid, ['primary', 'regulationPrice'], []) : [],
                hidePrice
            ),
            ...(!isFRR ? { reserveBidIdentification: editBid ? existingBid.reserveBidIdentification : null } : {}),
            bidType: null,
            isExternal: false,
        }));
    }, []);

    useEffect(() => {
        if (instanceBundle.value) getApprovedCapacity();
    }, [instanceBundle.value]);

    useEffect(() => {
        if (instanceBundle?.value && !editBid) getOtherMarketsBids();
    }, [date, selectedBidType?.value]);

    useEffect(() => {
        if (instanceBundle?.value) getBidRequest();
        if (market?.includes('aFRR') || market?.includes('mFRR')) getDatahubCostEnergy();
    }, [date]);

    useEffect(() => {
        if (selectedBidType?.value && instanceBundle.value) getLastBid();
    }, [selectedBidType?.value]);

    useEffect(() => {
        const temp = getInitialValues(
            date,
            market,
            pairs,
            lodashGet(lastBidData, ['capacity'], {}),
            lodashGet(lastBidData, ['price'], []),
            lodashGet(lastBidData, ['regulationPrice'], []),
            hidePrice
        );

        const tempCopy = cloneDeep(temp);

        if (alreadyBidCreatedHours.length) {
            alreadyBidCreatedHours.map((h) => {
                lodashSet(tempCopy, ['price', h], 0);
                isFRR && lodashSet(tempCopy, ['regulationPrice', h], 0);
                for (const key in lodashGet(tempCopy, ['capacity'], {})) {
                    lodashSet(tempCopy, ['capacity', key, h], 0);
                }
            });
        }
        setInitialValues((prev) => ({ ...prev, ...tempCopy }));
    }, [alreadyBidCreatedHours, lastBidData]);

    const getApprovedCapacity = () => {
        service.GetApprovedCapacity(instanceBundle.value, startLoader, handleApprovedCapacitySuccess, handleError, stopLoader);
    };

    const handleApprovedCapacitySuccess = ({ data }) => {
        setApprovedCapacity(data.data || {});
    };

    const getOtherMarketsBids = () => {
        service.GetOtherMarketsBid(
            instanceBundle.value,
            { date: momentTimeFormater(date).format('YYYY-MM-DD'), type: selectedBidType.value === 'secondary' ? 'both' : selectedBidType.value },
            startLoader,
            handleOtherMarketsBidSuccess,
            handleError,
            stopLoader
        );
    };

    const handleOtherMarketsBidSuccess = ({ data }) => {
        setAlreadyBidCreatedBidsHours(lodashGet(data, ['data'], []));
    };

    const getBidRequest = () => {
        service.GetBidRequest(
            instanceBundle.value,
            {
                date: momentTimeFormater(date).format('YYYY-MM-DD'),
                market,
            },
            startLoader,
            handleBidRequestSuccess,
            handleError,
            stopLoader
        );
    };

    const handleBidRequestSuccess = ({ data }) => {
        const temp = { note: null };
        if (lodashGet(data, ['data'], []).length) temp = { note: lodashGet(data, ['data']) };
        setBidRequest(temp);
    };

    const getLastBid = () => {
        service.GetLastBid(instanceBundle.value, { type: selectedBidType.value }, startLoader, handleGetLastBidSuccess, handleError, stopLoader);
    };

    const handleGetLastBidSuccess = ({ data }) => {
        const bundleBid = lodashGet(data, ['data', 'bundleBid']);
        if (bundleBid && !lodashGet(existingBid, ['price'], []).length) {
            const capacity = {};
            const tempDate = moment(bundleBid.date).add(1, 'day').toDate();
            if (!bidDate) setDate(tempDate);
            lodashGet(bundleBid, ['bids'], []).map((bid) => {
                lodashSet(capacity, [bid.instanceId], lodashGet(bid, [selectedBidType.value, 'capacity'], []));
            });
            const price = lodashGet(bundleBid, [selectedBidType.value, 'price'], []);

            const regulationPrice = lodashGet(bundleBid, [selectedBidType.value, 'regulationPrice'], []);
            if (price.length) {
                const capAndPrice = getInitialValues(tempDate, market, pairs, capacity, price, regulationPrice, hidePrice);
                setInitialValues({ ...initialValues, ...capAndPrice, reserveBidIdentification: bundleBid.reserveBidIdentification });
            }
            setLastBidData({ capacity, price, regulationPrice });
        } else {
            const data = {
                capacity: editBid ? lodashGet(existingBid, ['capacity'], {}) : {},
                price: editBid ? lodashGet(existingBid, ['price'], []) : [],
                regulationPrice: editBid ? lodashGet(existingBid, ['regulationPrice'], []) : [],
            };
            setLastBidData(data);
        }
    };

    const handleSubmit = (val) => {
        if (!editBid) {
            const payload = {
                ...val,
                bidType: selectedBidType.value,
                date: momentTimeFormater(date).format('YYYY-MM-DD'),
                market,
                instanceBundleId: instanceBundle.value,
                approvedCapacity: approvedCapacity,
            };
            service.CreateBid(payload, startLoader, handleCreateBidSuccess, handleError, stopLoader);
        } else {
            editFunc({
                ...val,
                instanceBundleId: instanceBundle.value,
            });
            closeModal();
        }
    };

    const handleCreateBidSuccess = () => {
        if (data.data?.status === 'bids already present') {
            setAlreadyBidCreatedBids(data.data.instanceIds);
            toast.error('Bids are already created.');
        } else {
            toast.success('Bid Created Successfully');
            getBids();
            closeModal();
        }
    };

    const getDatahubCostEnergy = () => {
        service.GetDatahubCostEnergy(
            { date: momentTimeFormater(date).format('YYYY-MM-DD'), instanceBundleId: instanceBundle.value },
            startLoader,
            handleGetDatahubCostEnergy,
            (err) => {
                handleError(err);
                setDatahubCostEnergy([]);
            },
            stopLoader
        );
    };

    const handleGetDatahubCostEnergy = ({ data }) => {
        setDatahubCostEnergy(lodashGet(data, ['data'], []));
    };

    const handleError = (err) => toast.error(lodashGet(err, ['response', 'data', 'message']));

    return (
        <div>
            <div>
                <Typography content={`${market} Bidding`} size="16" />
                <div className={bidClasses.FormWrapper}>
                    <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        innerRef={formikRef}
                        validationSchema={CreateBundleBidValidator(market, approvedCapacity, pairs, hidePrice)}
                    >
                        {({ values }) => {
                            return (
                                <Form>
                                    <div className={allDeviceClasses.InputContainer}>
                                        {!editBid && (
                                            <>
                                                <div className={allDeviceClasses.FieldControl}>
                                                    <label>
                                                        Date <span className="required">*</span>
                                                    </label>
                                                    <div className="modal-date-picker">
                                                        <DatePicker date={date} setDate={setDate} disableFutureDate={false} />
                                                    </div>
                                                </div>
                                                {['FCR-D-INC', 'FCR-D-DEC'].includes(market) && (
                                                    <div className={allDeviceClasses.FieldControl}>
                                                        <label>
                                                            Bid Type <span className="required">*</span>
                                                        </label>
                                                        <div>
                                                            <Dropdown name="bidType" options={BID_TYPES} defaultValue={selectedBidType} onChange={setSelectedBidType} />
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {['FCR-D-INC', 'FCR-D-DEC', 'FFR'].includes(market) && (
                                            <div className={allDeviceClasses.FieldControl}>
                                                <label>
                                                    Reserve Bid Identification
                                                    <span className="required">*</span>
                                                </label>
                                                <Input name="reserveBidIdentification" id="reserveBidIdentification" style={{ marginTop: '0' }} />
                                            </div>
                                        )}
                                        <div className={allDeviceClasses.FieldControl}>
                                            <label>Bid Manage Type</label>
                                            <ToggleButton values={values} label={'External'} name={'isExternal'} />
                                        </div>
                                    </div>
                                    {bidRequest.note && (
                                        <div className={allDeviceClasses.FieldControl2}>
                                            <label>Note</label>
                                            <TextArea name={`note`} label="Note" disabled={true} value={bidRequest.note.join('\n')} style={{ fontSize: '0.8vw' }} />
                                        </div>
                                    )}

                                    {[
                                        {
                                            label: 'Capacity (Mw)',
                                            value: 'capacity',
                                        },
                                        ...(hidePrice
                                            ? []
                                            : [
                                                  {
                                                      label: 'Price (€)',
                                                      value: 'price',
                                                  },
                                              ]),
                                        ...(isFRR
                                            ? [
                                                  {
                                                      label: 'Regulation Price (€)',
                                                      value: 'regulationPrice',
                                                  },
                                              ]
                                            : []),
                                    ].map((type) => (
                                        <div>
                                            {type.value === 'capacity' ? (
                                                <div>
                                                    {pairs?.map((el, i) => (
                                                        <div key={i}>
                                                            <div>
                                                                <div className={bidClasses.Subtitle}>
                                                                    Capacity(Mw) - {el.instanceName}{' '}
                                                                    {alreadyBidCreatedBids.includes(el.instanceId) && <span className="required">Bid is already created.</span>}
                                                                </div>
                                                            </div>
                                                            <div className={bidClasses.CreateBidForm}>
                                                                {lodashGet(values, ['capacity', el.instanceId], []).map((val, index) => (
                                                                    <div>
                                                                        <div className={allDeviceClasses.FieldControl}>
                                                                            <label htmlFor={`${type.value}.${index}`}>
                                                                                Slot {index + 1} {' / Hour '}
                                                                                {momentTimeFormater(date).startOf('day').clone().add(index, 'hour').hour()}-
                                                                                {momentTimeFormater(date)
                                                                                    .startOf('day')
                                                                                    .clone()
                                                                                    .add(index + 1, 'hour')
                                                                                    .hour()}
                                                                                <span className="required">*</span>
                                                                            </label>

                                                                            <Input
                                                                                name={`capacity.${el.instanceId}.${index}`}
                                                                                id={`capacity.${el.instanceId}.${index}`}
                                                                                type={'number'}
                                                                                style={{ marginTop: '0' }}
                                                                                step="0.1" // increasing or decreasing number by 0.1
                                                                                disabled={alreadyBidCreatedHours.includes(index) ? true : false}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={bidClasses.Subtitle}>{type.label}</div>
                                                    <div className={bidClasses.CreateBidForm}>
                                                        {lodashGet(values, [type.value]).map((val, index) => (
                                                            <div>
                                                                <div className={allDeviceClasses.FieldControl}>
                                                                    <label htmlFor={`${type.value}.${index}`}>
                                                                        Slot {index + 1} {' / Hour '}
                                                                        {momentTimeFormater(date).startOf('day').clone().add(index, 'hour').hour()}-
                                                                        {momentTimeFormater(date)
                                                                            .startOf('day')
                                                                            .clone()
                                                                            .add(index + 1, 'hour')
                                                                            .hour()}
                                                                        {type.value === 'price' ? ' Price' : ' '}
                                                                        <span className="required">*</span>
                                                                        {type.value === 'regulationPrice' && (datahubCostEnergy.length || cost > 0) ? (
                                                                            <span className="required"> [{lodashGet(datahubCostEnergy, [index], 0) + cost}]</span>
                                                                        ) : (
                                                                            ''
                                                                        )}
                                                                    </label>
                                                                    <Input
                                                                        name={`${type.value}.${index}`}
                                                                        id={`${type.value}.${index}`}
                                                                        type={'number'}
                                                                        style={{ marginTop: '0' }}
                                                                        step="0.1" // increasing or decreasing number by 0.1
                                                                        disabled={alreadyBidCreatedHours.includes(index) ? true : false}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </Form>
                            );
                        }}
                    </Formik>
                </div>

                <div className={allDeviceClasses.ButtonContainer}>
                    <div>
                        <button type="button" className="btn-secondary" onClick={() => closeModal()}>
                            Cancel
                        </button>
                    </div>
                    <div>
                        <button type="submit" className="btn-primary" onClick={() => formikRef.current.submitForm()}>
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
